import type {ReactNode} from 'react';
import type {MenuProps} from 'antd';

import type {Permission} from '@services/auth';
import {env} from '@services/utils';
import {brand} from '@utils/brand';
import {type DomainLeaf, DOMAINS, type DomainSection} from '@routes/domains';

// Описание permission'а для подзаголовка шапки.
export const describePermission = (p: Permission | null): string => {
  if (!p) return '';
  const parts: string[] = [];
  if (p.organization?.title) parts.push(p.organization.title);
  if (p.theater?.title) parts.push(p.theater.title);
  if (p.event?.title) parts.push(p.event.title);
  if (p.country?.title) parts.push(p.country.title);
  return parts.join(' · ');
};

// Видимые секции — фильтруем листы по country, секцию прячем если все
// дети отфильтровались. Permission-фильтрацию подключим в шаге 6.
export type VisibleSection = DomainSection & {
  visibleChildren: readonly DomainLeaf[];
};

export const buildVisibleSections = (): readonly VisibleSection[] => {
  const country = env.VITE_COUNTRY_CODE;
  return DOMAINS.flatMap((section): VisibleSection[] => {
    const visibleChildren = section.children.filter(
      (leaf) => leaf.enabled !== false && (!leaf.countries || leaf.countries.includes(country)),
    );
    if (section.children.length > 0 && visibleChildren.length === 0) return [];
    return [{...section, visibleChildren}];
  });
};

// AntD Menu items из доменов: секция без детей = простой пункт,
// с детьми = SubMenu.
//
// `renderLeafLabel` — хук для UI-обёртки листа (кнопка «закрепить»).
// Утилита остаётся чистой: JSX живёт в компоненте, сюда приходит
// готовый ReactNode.
export const buildMenuItems = (
  sections: readonly VisibleSection[],
  renderLeafLabel?: (key: string, title: string) => ReactNode,
): MenuProps['items'] =>
  sections.map((section) => {
    if (section.visibleChildren.length === 0) {
      return {key: section.id, icon: section.icon, label: section.title};
    }
    return {
      key: section.id,
      icon: section.icon,
      label: section.title,
      children: section.visibleChildren.map((leaf) => {
        const key = `${section.id}/${leaf.id}`;
        return {
          key,
          label: renderLeafLabel ? renderLeafLabel(key, leaf.title) : leaf.title,
        };
      }),
    };
  });

/** Лист меню по ключу `section/leaf` — для секции «Закреплённые». */
export const findLeafByKey = (
  key: string,
  sections: readonly VisibleSection[],
): {leaf: DomainLeaf; section: VisibleSection} | null => {
  if (!key.includes('/')) return null;
  const [sectionId, leafId] = key.split('/');
  const section = sections.find((s) => s.id === sectionId);
  const leaf = section?.visibleChildren.find((l) => l.id === leafId);
  return section && leaf ? {leaf, section} : null;
};

// Активные элементы для подсветки + раскрытия SubMenu по pathname.
type ActiveMatch = {
  section: VisibleSection | undefined;
  leaf: DomainLeaf | undefined;
};

export const activeFromPath = (
  pathname: string,
  sections: readonly VisibleSection[],
): ActiveMatch => {
  const segments = pathname.split('/').filter(Boolean);
  const sectionSeg = segments[0] ?? 'home';
  const leafSeg = segments[1];

  const section =
    sections.find((s) => s.id === sectionSeg) ?? sections.find((s) => s.id === 'home');
  if (!section) return {section: undefined, leaf: undefined};

  const leaf = leafSeg ? section.visibleChildren.find((c) => c.id === leafSeg) : undefined;
  return {section, leaf};
};

// Заголовок шапки.
export const buildHeaderTitle = (match: ActiveMatch): string =>
  match.leaf?.title ?? match.section?.title ?? brand.name;

// Куда переходить по клику на пункт. Для key вида 'section/leaf' — на leaf.
// Для key секции с детьми — на первый видимый ребёнок. Без детей — на path секции.
export const resolveNavTarget = (
  key: string,
  sections: readonly VisibleSection[],
): string | null => {
  if (key.includes('/')) {
    const [sectionId, leafId] = key.split('/');
    const section = sections.find((s) => s.id === sectionId);
    const leaf = section?.visibleChildren.find((l) => l.id === leafId);
    return leaf?.path ?? null;
  }
  const section = sections.find((s) => s.id === key);
  if (!section) return null;
  if (section.visibleChildren.length > 0) return section.visibleChildren[0]?.path ?? null;
  return section.path;
};
